import { DerbyRngV1 } from '../rng';
import { DRILL_TICK_S, MASK_LEFT_PRESSED, MASK_PRESSED, MASK_RIGHT_PRESSED, type MinigameDef, type MinigameSession, type SessionScore } from './framework';

export type GearCue = 'up' | 'left' | 'right';
export interface GearPrompt { tick: number; cue: GearCue; hit: boolean | null }

/** Acceleration sequences: execute rapid gear calls without a wrong step. */
export class GearsSession implements MinigameSession {
  readonly durationTicks = Math.round(15 / DRILL_TICK_S);
  readonly prompts: GearPrompt[];
  tick = 0;
  hits = 0;
  errors = 0;
  combo = 0;
  bestCombo = 0;
  private cursor = 0;

  constructor(readonly tier: number, seed: number) {
    const rng = new DerbyRngV1(seed).fork('gears');
    const cues: GearCue[] = ['up', 'left', 'right'];
    const prompts: GearPrompt[] = [];
    let at = 30;
    while (at < this.durationTicks - 8) {
      prompts.push({ tick: at, cue: rng.pick(cues), hit: null });
      at += Math.round(rng.range(0.62 - tier * 0.035, 0.92 - tier * 0.035) / DRILL_TICK_S);
    }
    this.prompts = prompts;
  }

  get finished(): boolean { return this.tick >= this.durationTicks; }
  nextPrompt(): GearPrompt | null { return this.prompts[this.cursor] ?? null; }
  nextPromptIndex(): number { return this.cursor; }
  timingWindowTicks(): number { return Math.max(3, 7 - this.tier); }
  private inputCue(mask: number): GearCue | null {
    if ((mask & MASK_PRESSED) !== 0) return 'up';
    if ((mask & MASK_LEFT_PRESSED) !== 0) return 'left';
    if ((mask & MASK_RIGHT_PRESSED) !== 0) return 'right';
    return null;
  }

  step(mask: number): void {
    if (this.finished) return;
    const window = this.timingWindowTicks();
    const cue = this.inputCue(mask);
    const prompt = this.prompts[this.cursor];
    if (cue) {
      if (prompt && Math.abs(this.tick - prompt.tick) <= window && cue === prompt.cue) {
        prompt.hit = true; this.hits += 1; this.combo += 1; this.bestCombo = Math.max(this.bestCombo, this.combo); this.cursor += 1;
      } else {
        this.errors += 1; this.combo = 0;
      }
    }
    const due = this.prompts[this.cursor];
    if (due && this.tick > due.tick + window) { due.hit = false; this.errors += 1; this.combo = 0; this.cursor += 1; }
    this.tick += 1;
  }

  score(): SessionScore {
    const accuracy = this.prompts.length ? this.hits / this.prompts.length : 0;
    const raw = Math.max(0, Math.min(1, accuracy + Math.min(0.08, this.bestCombo * 0.006) - this.errors * 0.01));
    return { raw, detail: `${this.hits}/${this.prompts.length} changes · combo ×${this.bestCombo} · ${this.errors} errors`, failed: false };
  }
}

export const GEARS_DEF: MinigameDef = {
  id: 'gears', name: 'Gear Changes', stat: 'accel', energyCost: 4,
  create: (tier, seed) => new GearsSession(tier, seed),
};
